import React, { useState, useEffect, useCallback } from 'react';
import {
  getAnalyticsKpi,
  getAnalyticsForecast,
  getAnalyticsRisk,
  getAnalyticsConsumption,
  getAnalyticsTopFlop,
  getAnalyticsEfficiency,
  getAnalyticsExpenses,
  getAnalyticsGeographic,
  getAnalyticsRecommendations,
  getPartenaires,
} from '../services/api';

// ─── Styles ───
const S: Record<string, React.CSSProperties> = {
  page: { padding: 0 },
  header: { marginBottom: 24 },
  title: { fontSize: 26, fontWeight: 800, color: '#1a237e', margin: 0 },
  subtitle: { fontSize: 14, color: '#666', marginTop: 4 },

  /* tabs */
  tabs: { display: 'flex', gap: 0, background: '#f0f0f0', borderRadius: 12, padding: 4, marginBottom: 24, flexWrap: 'wrap' as const },
  tab: { padding: '10px 18px', borderRadius: 10, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#555', transition: '.2s' },
  tabActive: { background: '#1a237e', color: '#fff', boxShadow: '0 2px 8px rgba(26,35,126,.3)' },

  /* filter bar */
  filterBar: { display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' as const },
  select: { padding: '8px 14px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, background: '#fff' },

  /* KPI cards */
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 18 },
  kpiCard: { background: '#fff', borderRadius: 12, padding: '14px 14px', boxShadow: '0 2px 10px rgba(0,0,0,.05)', position: 'relative' as const, overflow: 'hidden' },
  kpiLabel: { fontSize: 10, color: '#888', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '.5px' },
  kpiValue: { fontSize: 20, fontWeight: 800, color: '#1a237e', marginTop: 4 },
  kpiUnit: { fontSize: 11, fontWeight: 400, color: '#999', marginLeft: 3 },
  kpiVariation: { fontSize: 10, fontWeight: 700, marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 7px', borderRadius: 20 },

  /* section */
  section: { marginBottom: 32 },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: '#1a237e', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 },
  sectionIcon: { fontSize: 22 },

  /* cards */
  card: { background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,.06)', marginBottom: 20 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: '#333', marginBottom: 16 },

  /* chart container */
  chartArea: { width: '100%', overflowX: 'auto' as const },

  /* bar chart */
  barRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 },
  barLabel: { width: 140, fontSize: 12, color: '#555', textAlign: 'right' as const, flexShrink: 0 },
  barTrack: { flex: 1, height: 28, background: '#f0f0f5', borderRadius: 6, position: 'relative' as const, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 6, transition: 'width .6s ease' },
  barValue: { position: 'absolute' as const, right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, fontWeight: 700, color: '#fff' },

  /* table */
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { padding: '10px 12px', textAlign: 'left' as const, borderBottom: '2px solid #eee', fontWeight: 700, color: '#555', fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: '.5px' },
  td: { padding: '10px 12px', borderBottom: '1px solid #f5f5f5', color: '#333' },

  /* risk badge */
  badge: { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 },

  /* recommendation card */
  recoCard: { display: 'flex', gap: 16, padding: 18, borderRadius: 12, marginBottom: 12, alignItems: 'flex-start' },
  recoIcon: { width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 },
  recoContent: { flex: 1 },
  recoTitle: { fontWeight: 700, fontSize: 14, marginBottom: 4 },
  recoDesc: { fontSize: 13, color: '#555', lineHeight: 1.5 },
  recoBar: { marginTop: 8, height: 6, background: '#eee', borderRadius: 3, overflow: 'hidden' },
  recoFill: { height: '100%', borderRadius: 3, transition: 'width .6s ease' },

  /* mini chart (SVG) */
  miniChart: { width: '100%', height: 220 },

  /* grid layouts */
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 },

  /* loading */
  loading: { textAlign: 'center' as const, padding: 60, color: '#999' },
  spinner: { display: 'inline-block', width: 36, height: 36, border: '3px solid #eee', borderTop: '3px solid #1a237e', borderRadius: '50%', animation: 'spin 1s linear infinite' },

  /* empty */
  empty: { textAlign: 'center' as const, padding: 40, color: '#aaa', fontSize: 14 },

  /* gauge */
  gaugeWrap: { display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 4 },
};

const MONTHS = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (m: number) => MONTHS[(m >= 1 && m <= 12) ? m - 1 : 0] || `M${m}`;

const TABS = [
  { key: 'overview', label: 'Vue d\'ensemble', icon: '\u{1F4CA}' },
  { key: 'forecast', label: 'Previsions', icon: '\u{1F52E}' },
  { key: 'risk', label: 'Risques paiement', icon: '\u26A0\uFE0F' },
  { key: 'consumption', label: 'Consommation', icon: '\u{1F4A7}' },
  { key: 'topflop', label: 'Top & Flop', icon: '\u{1F3C6}' },
  { key: 'efficiency', label: 'Efficacite', icon: '\u2699\uFE0F' },
  { key: 'expenses', label: 'Charges', icon: '\u{1F4B8}' },
  { key: 'geographic', label: 'Geographique', icon: '\u{1F4CD}' },
];

function fmt(n: number, unit?: string): string {
  if (unit === '%') return n.toFixed(1) + '%';
  if (Math.abs(n) >= 1e9) return (n / 1e9).toFixed(1) + ' Md';
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(1) + ' M';
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(1) + ' K';
  return n.toLocaleString('fr-FR');
}

function fmtFull(n: number): string {
  return n.toLocaleString('fr-FR');
}

const RISK_COLORS: Record<string, { bg: string; text: string }> = {
  critique: { bg: '#ffebee', text: '#c62828' },
  eleve: { bg: '#fff3e0', text: '#e65100' },
  moyen: { bg: '#fff8e1', text: '#f9a825' },
  faible: { bg: '#e8f5e9', text: '#2e7d32' },
};

const RECO_STYLES: Record<string, { bg: string; iconBg: string; icon: string }> = {
  alerte: { bg: '#fff5f5', iconBg: '#e53e3e', icon: '\u{1F6A8}' },
  attention: { bg: '#fffbeb', iconBg: '#dd6b20', icon: '\u26A0\uFE0F' },
  recommandation: { bg: '#ebf8ff', iconBg: '#3182ce', icon: '\u{1F4A1}' },
  succes: { bg: '#f0fff4', iconBg: '#38a169', icon: '\u2705' },
};

// ─── SVG mini chart component ───
const MiniLineChart: React.FC<{
  data: { label: string; values: { value: number; color: string; label: string }[] }[];
  height?: number;
}> = ({ data, height = 200 }) => {
  if (!data.length) return null;
  const W = Math.max(600, data.length * 60);
  const H = height;
  const PAD = { top: 30, right: 20, bottom: 40, left: 70 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const seriesCount = data[0].values.length;
  const allVals = data.flatMap(d => d.values.map(v => v.value));
  const maxVal = Math.max(...allVals, 1);
  const minVal = Math.min(...allVals.filter(v => v > 0), 0);

  const getX = (i: number) => PAD.left + (i / Math.max(data.length - 1, 1)) * chartW;
  const getY = (v: number) => PAD.top + chartH - ((v - minVal) / (maxVal - minVal || 1)) * chartH;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H, display: 'block' }}>
      {/* Grid lines */}
      {[0, .25, .5, .75, 1].map(pct => {
        const y = PAD.top + chartH * (1 - pct);
        const val = minVal + (maxVal - minVal) * pct;
        return (
          <g key={pct}>
            <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y} stroke="#eee" strokeWidth={1} />
            <text x={PAD.left - 8} y={y + 4} textAnchor="end" fill="#999" fontSize={10}>{fmt(val)}</text>
          </g>
        );
      })}
      {/* Series */}
      {Array.from({ length: seriesCount }).map((_, si) => {
        const points = data.map((d, i) => `${getX(i)},${getY(d.values[si].value)}`).join(' ');
        const color = data[0].values[si].color;
        return (
          <g key={si}>
            <polyline points={points} fill="none" stroke={color} strokeWidth={2.5} strokeLinejoin="round" />
            {data.map((d, i) => (
              <circle key={i} cx={getX(i)} cy={getY(d.values[si].value)} r={3.5} fill={color} stroke="#fff" strokeWidth={1.5} />
            ))}
          </g>
        );
      })}
      {/* X labels */}
      {data.map((d, i) => (
        <text key={i} x={getX(i)} y={H - 8} textAnchor="middle" fill="#888" fontSize={10}>{d.label}</text>
      ))}
      {/* Legend */}
      {data[0].values.map((v, si) => (
        <g key={si} transform={`translate(${PAD.left + si * 140}, 14)`}>
          <rect width={12} height={12} rx={3} fill={v.color} />
          <text x={16} y={10} fill="#555" fontSize={11} fontWeight={600}>{v.label}</text>
        </g>
      ))}
    </svg>
  );
};

// ─── Bar chart component ───
const HBar: React.FC<{ label: string; value: number; max: number; color: string; suffix?: string }> = ({ label, value, max, color, suffix = '' }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={S.barRow}>
      <div style={S.barLabel}>{label}</div>
      <div style={S.barTrack}>
        <div style={{ ...S.barFill, width: `${pct}%`, background: color }} />
        <div style={{ ...S.barValue, color: pct > 30 ? '#fff' : '#333' }}>{fmt(value)}{suffix}</div>
      </div>
    </div>
  );
};

// ─── Donut ───
const Donut: React.FC<{ segments: { label: string; value: number; color: string }[]; size?: number }> = ({ segments, size = 160 }) => {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) return <div style={S.empty}>Aucune donnee</div>;
  const cx = size / 2, cy = size / 2, r = size / 2 - 10, inner = r * 0.55;
  let angle = -90;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
      <svg width={size} height={size}>
        {segments.map((seg, i) => {
          const pct = seg.value / total;
          const sweep = pct * 360;
          const startRad = (angle * Math.PI) / 180;
          const endRad = ((angle + sweep) * Math.PI) / 180;
          const large = sweep > 180 ? 1 : 0;
          const x1 = cx + r * Math.cos(startRad), y1 = cy + r * Math.sin(startRad);
          const x2 = cx + r * Math.cos(endRad), y2 = cy + r * Math.sin(endRad);
          const ix1 = cx + inner * Math.cos(endRad), iy1 = cy + inner * Math.sin(endRad);
          const ix2 = cx + inner * Math.cos(startRad), iy2 = cy + inner * Math.sin(startRad);
          const path = `M${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2} L${ix1},${iy1} A${inner},${inner} 0 ${large} 0 ${ix2},${iy2} Z`;
          angle += sweep;
          return <path key={i} d={path} fill={seg.color} stroke="#fff" strokeWidth={2} />;
        })}
        <text x={cx} y={cy - 4} textAnchor="middle" fill="#333" fontSize={16} fontWeight={800}>{fmtFull(total)}</text>
        <text x={cx} y={cy + 14} textAnchor="middle" fill="#888" fontSize={10}>TOTAL</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {segments.map((seg, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: seg.color, flexShrink: 0 }} />
            <span style={{ color: '#555' }}>{seg.label}</span>
            <span style={{ fontWeight: 700, color: '#333', marginLeft: 'auto' }}>{fmtFull(seg.value)}</span>
            <span style={{ color: '#999', fontSize: 11 }}>({(seg.value / total * 100).toFixed(1)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Gauge ───
const Gauge: React.FC<{ value: number; max?: number; label: string; color: string }> = ({ value, max = 100, label, color }) => {
  const pct = Math.min(value / max, 1);
  const r = 50, cx = 60, cy = 60;
  const startAngle = -210, endAngle = 30;
  const sweep = (endAngle - startAngle) * pct;
  const toRad = (a: number) => (a * Math.PI) / 180;
  const arcPath = (sa: number, ea: number) => {
    const x1 = cx + r * Math.cos(toRad(sa)), y1 = cy + r * Math.sin(toRad(sa));
    const x2 = cx + r * Math.cos(toRad(ea)), y2 = cy + r * Math.sin(toRad(ea));
    const large = ea - sa > 180 ? 1 : 0;
    return `M${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2}`;
  };
  return (
    <div style={S.gaugeWrap}>
      <svg width={120} height={80} viewBox="0 0 120 80">
        <path d={arcPath(startAngle, endAngle)} fill="none" stroke="#eee" strokeWidth={10} strokeLinecap="round" />
        <path d={arcPath(startAngle, startAngle + sweep)} fill="none" stroke={color} strokeWidth={10} strokeLinecap="round" />
        <text x={cx} y={cy + 2} textAnchor="middle" fill="#333" fontSize={18} fontWeight={800}>{value.toFixed(1)}%</text>
      </svg>
      <div style={{ fontSize: 11, color: '#666', fontWeight: 600, textAlign: 'center' }}>{label}</div>
    </div>
  );
};


// ══════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════
const Analytics: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [partenaireId, setPartenaireId] = useState('');
  const [partenaires, setPartenaires] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Data states
  const [kpis, setKpis] = useState<any>(null);
  const [forecast, setForecast] = useState<any>(null);
  const [risk, setRisk] = useState<any>(null);
  const [consumption, setConsumption] = useState<any>(null);
  const [topflop, setTopflop] = useState<any>(null);
  const [efficiency, setEfficiency] = useState<any>(null);
  const [expenses, setExpenses] = useState<any>(null);
  const [geographic, setGeographic] = useState<any>(null);
  const [recommendations, setRecommendations] = useState<any>(null);
  const [tfSubTab, setTfSubTab] = useState('partenaires');

  useEffect(() => {
    getPartenaires().then(r => setPartenaires(r.data.data || r.data || [])).catch(() => {});
  }, []);

  const params = partenaireId ? { partenaire_id: partenaireId } : undefined;

  const loadTab = useCallback(async (tab: string) => {
    setLoading(true);
    try {
      switch (tab) {
        case 'overview':
          const [kpiRes, recoRes] = await Promise.all([
            getAnalyticsKpi(params),
            getAnalyticsRecommendations(params),
          ]);
          setKpis(kpiRes.data);
          setRecommendations(recoRes.data);
          break;
        case 'forecast':
          const fRes = await getAnalyticsForecast(params);
          setForecast(fRes.data);
          break;
        case 'risk':
          const rRes = await getAnalyticsRisk(params);
          setRisk(rRes.data);
          break;
        case 'consumption':
          const cRes = await getAnalyticsConsumption(params);
          setConsumption(cRes.data);
          break;
        case 'topflop':
          const tfRes = await getAnalyticsTopFlop(params);
          setTopflop(tfRes.data);
          break;
        case 'efficiency':
          const eRes = await getAnalyticsEfficiency(params);
          setEfficiency(eRes.data);
          break;
        case 'expenses':
          const xRes = await getAnalyticsExpenses(params);
          setExpenses(xRes.data);
          break;
        case 'geographic':
          const gRes = await getAnalyticsGeographic(params);
          setGeographic(gRes.data);
          break;
      }
    } catch (e) {
      console.error('Analytics load error:', e);
    }
    setLoading(false);
  }, [partenaireId]);

  useEffect(() => { loadTab(activeTab); }, [activeTab, partenaireId, loadTab]);

  const CHART_COLORS = ['#1a237e', '#43a047', '#e53935', '#fb8c00', '#8e24aa', '#00acc1', '#6d4c41', '#546e7a'];

  // ─── RENDER FUNCTIONS ───

  const renderOverview = () => {
    if (!kpis) return null;
    return (
      <>
        <div style={S.kpiGrid}>
          {kpis.kpis?.map((k: any, i: number) => {
            const isUp = k.variation > 0;
            const isNeutral = Math.abs(k.variation) < 0.5;
            const variColor = isNeutral ? '#888' : (k.icon === 'charges' ? (isUp ? '#e53935' : '#43a047') : (isUp ? '#43a047' : '#e53935'));
            return (
              <div key={i} style={S.kpiCard}>
                <div style={S.kpiLabel}>{k.label}</div>
                <div style={S.kpiValue}>
                  {k.unit === '%' ? k.value.toFixed(1) : fmt(k.value)}
                  <span style={S.kpiUnit}>{k.unit}</span>
                </div>
                <div style={{ ...S.kpiVariation, background: `${variColor}15`, color: variColor }}>
                  {isUp ? '\u25B2' : isNeutral ? '\u25AC' : '\u25BC'} {Math.abs(k.variation).toFixed(1)}%
                  <span style={{ fontWeight: 400, fontSize: 10, marginLeft: 4 }}>vs mois prec.</span>
                </div>
                <div style={{ position: 'absolute', top: 0, right: 0, width: 4, height: '100%', background: CHART_COLORS[i % CHART_COLORS.length], borderRadius: '0 14px 14px 0' }} />
              </div>
            );
          })}
        </div>

        {recommendations?.recommendations?.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1a237e', display: 'flex', alignItems: 'center', gap: 8 }}>
                {'\u{1F4A1}'} Recommandations intelligentes
                <span style={{ fontSize: 11, fontWeight: 500, color: '#999', background: '#f5f5f5', padding: '2px 8px', borderRadius: 10 }}>
                  {recommendations.recommendations.length}
                </span>
              </div>
              <div style={{ fontSize: 11, color: '#aaa' }}>{'\u2190'} glisser {'\u2192'}</div>
            </div>
            <div style={{
              display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 8,
              scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin' as any,
            }}>
              {recommendations.recommendations.map((r: any, i: number) => {
                const rs = RECO_STYLES[r.type] || RECO_STYLES.recommandation;
                const impactColors: Record<string, string> = { eleve: '#e53935', moyen: '#fb8c00', positif: '#43a047', faible: '#66bb6a' };
                return (
                  <div key={i} style={{
                    minWidth: 280, maxWidth: 320, flex: '0 0 auto', scrollSnapAlign: 'start',
                    background: '#fff', borderRadius: 14, padding: 0, overflow: 'hidden',
                    boxShadow: '0 2px 12px rgba(0,0,0,.07)', border: `1px solid ${rs.iconBg}20`,
                    display: 'flex', flexDirection: 'column',
                  }}>
                    {/* Card header with colored stripe */}
                    <div style={{
                      background: `linear-gradient(135deg, ${rs.iconBg}, ${rs.iconBg}dd)`,
                      padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10,
                    }}>
                      <span style={{ fontSize: 22 }}>{rs.icon}</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>{r.titre}</div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,.7)', marginTop: 2 }}>{r.categorie}</div>
                      </div>
                    </div>
                    {/* Card body */}
                    <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6, flex: 1 }}>{r.description}</div>
                      {/* Progress bar */}
                      {r.valeur !== undefined && r.objectif !== undefined && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#888', marginBottom: 4 }}>
                            <span>Actuel: {r.valeur}%</span>
                            <span>Objectif: {r.objectif}%</span>
                          </div>
                          <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ height: '100%', borderRadius: 3, width: `${Math.min(r.valeur / r.objectif * 100, 100)}%`, background: rs.iconBg, transition: 'width .6s ease' }} />
                          </div>
                        </div>
                      )}
                      {/* Impact badge */}
                      {r.impact && (
                        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                            background: `${impactColors[r.impact] || '#888'}15`,
                            color: impactColors[r.impact] || '#888',
                            textTransform: 'uppercase', letterSpacing: '.5px',
                          }}>
                            Impact {r.impact}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </>
    );
  };

  const renderForecast = () => {
    if (!forecast) return null;
    if (forecast.message) return <div style={S.empty}>{forecast.message}</div>;

    const allData = [
      ...forecast.historical.map((h: any) => ({
        label: `${monthLabel(h.mois)} ${h.annee}`,
        values: [
          { value: h.facturation, color: '#1a237e', label: 'Facturation' },
          { value: h.recouvrement, color: '#43a047', label: 'Recouvrement' },
        ]
      })),
      ...forecast.predictions.map((p: any) => ({
        label: `${monthLabel(p.mois)} ${p.annee} *`,
        values: [
          { value: p.facturation, color: '#9fa8da', label: 'Facturation' },
          { value: p.recouvrement, color: '#a5d6a7', label: 'Recouvrement' },
        ]
      })),
    ];

    const tendanceLabel = forecast.tendance === 'hausse' ? '\u2197\uFE0F En hausse' : forecast.tendance === 'baisse' ? '\u2198\uFE0F En baisse' : '\u27A1\uFE0F Stable';
    const r2Fact = forecast.model?.facturation?.r2 || 0;

    return (
      <>
        <div style={{ ...S.card }}>
          <div style={S.cardTitle}>Prevision des revenus (6 prochains mois)</div>
          <div style={{ display: 'flex', gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ ...S.badge, background: '#e8eaf6', color: '#1a237e', fontSize: 13, padding: '6px 14px' }}>
              Tendance: {tendanceLabel}
            </div>
            <div style={{ ...S.badge, background: '#f5f5f5', color: '#555', fontSize: 13, padding: '6px 14px' }}>
              Fiabilite du modele (R2): {(r2Fact * 100).toFixed(1)}%
            </div>
          </div>
          <div style={S.chartArea}>
            <MiniLineChart data={allData.slice(-18)} height={260} />
          </div>
          <div style={{ fontSize: 11, color: '#999', marginTop: 8 }}>* Valeurs predites par regression lineaire</div>
        </div>

        <div style={{ ...S.grid2, marginTop: 20 }}>
          <div style={S.card}>
            <div style={S.cardTitle}>Previsions detaillees</div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Mois</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Facturation</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Recouvrement</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Volume (m3)</th>
                </tr>
              </thead>
              <tbody>
                {forecast.predictions.map((p: any, i: number) => (
                  <tr key={i}>
                    <td style={S.td}>{monthLabel(p.mois)} {p.annee}</td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>{fmtFull(p.facturation)} FCFA</td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 600, color: '#43a047' }}>{fmtFull(p.recouvrement)} FCFA</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{fmtFull(p.volume)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={S.card}>
            <div style={S.cardTitle}>Qualite du modele predictif</div>
            <div style={{ display: 'flex', gap: 30, justifyContent: 'center', marginTop: 20 }}>
              <Gauge value={(forecast.model?.facturation?.r2 || 0) * 100} label="Facturation" color="#1a237e" />
              <Gauge value={(forecast.model?.recouvrement?.r2 || 0) * 100} label="Recouvrement" color="#43a047" />
              <Gauge value={(forecast.model?.volume?.r2 || 0) * 100} label="Volume" color="#fb8c00" />
            </div>
            <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: '#888' }}>
              R2 mesure la precision du modele (100% = prediction parfaite)
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderRisk = () => {
    if (!risk) return null;
    const { data: risked, summary } = risk;
    return (
      <>
        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
          {[
            { label: 'Abonnes a risque', value: summary.totalAbonnesARisque, color: '#e53935' },
            { label: 'Risque critique', value: summary.critique, color: '#c62828' },
            { label: 'Risque eleve', value: summary.eleve, color: '#e65100' },
            { label: 'Risque moyen', value: summary.moyen, color: '#f9a825' },
            { label: 'Total impayes', value: summary.totalImpaye, color: '#e53935', isMoney: true },
          ].map((c, i) => (
            <div key={i} style={{ ...S.kpiCard, borderLeft: `4px solid ${c.color}` }}>
              <div style={S.kpiLabel}>{c.label}</div>
              <div style={{ ...S.kpiValue, color: c.color, fontSize: 22 }}>
                {c.isMoney ? fmt(c.value) + ' FCFA' : c.value}
              </div>
            </div>
          ))}
        </div>

        <div style={S.card}>
          <div style={S.cardTitle}>Abonnes a risque de defaut de paiement</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Abonne</th>
                  <th style={S.th}>Partenaire</th>
                  <th style={S.th}>Secteur</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Facture</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Paye</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Impaye</th>
                  <th style={{ ...S.th, textAlign: 'center' }}>Taux</th>
                  <th style={{ ...S.th, textAlign: 'center' }}>Score</th>
                  <th style={{ ...S.th, textAlign: 'center' }}>Risque</th>
                </tr>
              </thead>
              <tbody>
                {risked?.slice(0, 30).map((r: any, i: number) => {
                  const rc = RISK_COLORS[r.riskLevel] || RISK_COLORS.faible;
                  return (
                    <tr key={i}>
                      <td style={S.td}>
                        <div style={{ fontWeight: 600 }}>{r.nomComplet || r.prenomnom || r.reference}</div>
                        <div style={{ fontSize: 11, color: '#999' }}>{r.reference}</div>
                      </td>
                      <td style={S.td}>{r.partenaire}</td>
                      <td style={S.td}>{r.secteur}</td>
                      <td style={{ ...S.td, textAlign: 'right' }}>{fmtFull(r.totalFacture)}</td>
                      <td style={{ ...S.td, textAlign: 'right', color: '#43a047' }}>{fmtFull(r.totalPaye)}</td>
                      <td style={{ ...S.td, textAlign: 'right', color: '#e53935', fontWeight: 700 }}>{fmtFull(r.soldeImpaye)}</td>
                      <td style={{ ...S.td, textAlign: 'center' }}>{r.tauxPaiement}%</td>
                      <td style={{ ...S.td, textAlign: 'center', fontWeight: 700 }}>{r.riskScore}</td>
                      <td style={{ ...S.td, textAlign: 'center' }}>
                        <span style={{ ...S.badge, background: rc.bg, color: rc.text }}>{r.riskLevel}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  const renderConsumption = () => {
    if (!consumption) return null;
    const { seasonal, byCategory, topConsumers, distribution } = consumption;

    return (
      <>
        <div style={S.grid2}>
          {/* Seasonal pattern */}
          <div style={S.card}>
            <div style={S.cardTitle}>Saisonnalite de la consommation</div>
            {seasonal?.length > 0 && (
              <MiniLineChart
                data={seasonal.map((s: any) => ({
                  label: monthLabel(s.mois),
                  values: [
                    { value: s.consoMoyenne, color: '#1a237e', label: 'Moyenne' },
                    { value: s.consoMax, color: '#e53935', label: 'Max' },
                    { value: s.consoMin, color: '#43a047', label: 'Min' },
                  ]
                }))}
              />
            )}
          </div>

          {/* Distribution */}
          <div style={S.card}>
            <div style={S.cardTitle}>Distribution des consommations (m3)</div>
            {distribution?.map((d: any, i: number) => (
              <HBar
                key={i}
                label={`${d.tranche} m3`}
                value={d.nbFactures}
                max={Math.max(...distribution.map((x: any) => x.nbFactures))}
                color={CHART_COLORS[i % CHART_COLORS.length]}
              />
            ))}
          </div>
        </div>

        <div style={S.grid2}>
          {/* By category */}
          <div style={S.card}>
            <div style={S.cardTitle}>Consommation par categorie</div>
            <Donut
              segments={byCategory?.map((c: any, i: number) => ({
                label: c.categorie || 'Non classifie',
                value: c.consoTotale,
                color: CHART_COLORS[i % CHART_COLORS.length],
              })) || []}
            />
          </div>

          {/* Top consumers */}
          <div style={S.card}>
            <div style={S.cardTitle}>Top 10 consommateurs</div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Abonne</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Conso. totale</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Conso. moy.</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Factures</th>
                </tr>
              </thead>
              <tbody>
                {topConsumers?.slice(0, 10).map((t: any, i: number) => (
                  <tr key={i}>
                    <td style={S.td}>
                      <div style={{ fontWeight: 600 }}>{t.prenomnom}</div>
                      <div style={{ fontSize: 11, color: '#999' }}>{t.partenaire}</div>
                    </td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 700 }}>{fmtFull(t.consoTotale)} m3</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{t.consoMoyenne.toFixed(1)} m3</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{fmtFull(t.totalFacture)} FCFA</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  const renderTopFlop = () => {
    if (!topflop) return null;
    const { partenaires: parts, abonnes, secteurs, localites, periodes } = topflop;

    const TF_SUBTABS = [
      { key: 'partenaires', label: 'Partenaires', icon: '\u{1F3E2}' },
      { key: 'abonnes', label: 'Abonnes', icon: '\u{1F465}' },
      { key: 'secteurs', label: 'Secteurs', icon: '\u{1F4CD}' },
      { key: 'periodes', label: 'Periodes', icon: '\u{1F4C5}' },
      { key: 'localites', label: 'Localites', icon: '\u{1F3D8}\uFE0F' },
    ];

    const TFSection: React.FC<{ topTitle: string; flopTitle: string; topIcon: string; flopIcon: string; children: [React.ReactNode, React.ReactNode] }> = ({ topTitle, flopTitle, topIcon, flopIcon, children }) => (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div style={{ ...S.card, borderTop: '4px solid #43a047', marginBottom: 0 }}>
          <div style={{ ...S.cardTitle, color: '#2e7d32' }}>{topIcon} {topTitle}</div>
          {children[0]}
        </div>
        <div style={{ ...S.card, borderTop: '4px solid #e53935', marginBottom: 0 }}>
          <div style={{ ...S.cardTitle, color: '#c62828' }}>{flopIcon} {flopTitle}</div>
          {children[1]}
        </div>
      </div>
    );

    const rankBadge = (i: number, isTop: boolean) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', background: isTop ? (i < 3 ? ['#ffd700', '#c0c0c0', '#cd7f32'][i] : '#e8f5e9') : (i < 3 ? ['#e53935', '#ef5350', '#f44336'][i] : '#ffebee'), color: isTop ? (i < 3 ? '#fff' : '#2e7d32') : (i < 3 ? '#fff' : '#c62828'), fontSize: 12, fontWeight: 800, flexShrink: 0 }}>{i + 1}</span>
    );

    const TFRow: React.FC<{ rank: number; isTop: boolean; name: string; sub?: string; value: string; extra?: string; extraColor?: string }> = ({ rank, isTop, name, sub, value, extra, extraColor }) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #f5f5f5' }}>
        {rankBadge(rank, isTop)}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name || 'N/A'}</div>
          {sub && <div style={{ fontSize: 11, color: '#999' }}>{sub}</div>}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: isTop ? '#2e7d32' : '#c62828' }}>{value}</div>
          {extra && <div style={{ fontSize: 11, color: extraColor || '#888' }}>{extra}</div>}
        </div>
      </div>
    );

    const renderSubContent = () => {
      switch (tfSubTab) {
        case 'partenaires':
          return (
            <>
              <TFSection topTitle="Top Recouvrement" flopTitle="Flop Recouvrement" topIcon={'\u{1F3C6}'} flopIcon={'\u{1F4C9}'}>
                {[
                  <div key="t">
                    {parts?.topRecouvrement?.map((p: any, i: number) => (
                      <TFRow key={i} rank={i} isTop name={p.libelle} sub={`${p.nbAbonnes} abonnes`}
                        value={`${p.tauxRecouvrement}%`} extra={`${fmt(p.totalPaye)} / ${fmt(p.totalFacture)} FCFA`} />
                    ))}
                  </div>,
                  <div key="f">
                    {parts?.flopRecouvrement?.map((p: any, i: number) => (
                      <TFRow key={i} rank={i} isTop={false} name={p.libelle} sub={`${p.nbAbonnes} abonnes`}
                        value={`${p.tauxRecouvrement}%`} extra={`Impaye: ${fmt(p.impaye)} FCFA`} extraColor="#c62828" />
                    ))}
                  </div>
                ]}
              </TFSection>
              <div style={{ height: 20 }} />
              <TFSection topTitle="Top Facturation" flopTitle="Flop Marge nette" topIcon={'\u{1F4B0}'} flopIcon={'\u{1F4C9}'}>
                {[
                  <div key="t">
                    {parts?.topFacturation?.map((p: any, i: number) => (
                      <TFRow key={i} rank={i} isTop name={p.libelle} sub={`${p.nbAbonnes} abonnes`}
                        value={`${fmt(p.totalFacture)} FCFA`} extra={`Marge: ${fmt(p.margeNette)} FCFA`} />
                    ))}
                  </div>,
                  <div key="f">
                    {parts?.flopMarge?.map((p: any, i: number) => (
                      <TFRow key={i} rank={i} isTop={false} name={p.libelle} sub={`Charges: ${fmt(p.totalCharges)} FCFA`}
                        value={`${fmt(p.margeNette)} FCFA`} extra={`Recouv: ${fmt(p.totalPaye)} FCFA`} />
                    ))}
                  </div>
                ]}
              </TFSection>
            </>
          );

        case 'abonnes':
          return (
            <>
              <TFSection topTitle="Meilleurs payeurs" flopTitle="Plus gros debiteurs" topIcon={'\u2B50'} flopIcon={'\u{1F6A8}'}>
                {[
                  <div key="t">
                    {abonnes?.topPaye?.map((a: any, i: number) => (
                      <TFRow key={i} rank={i} isTop name={a.prenomnom || a.reference} sub={`${a.partenaire} - ${a.secteur || ''}`}
                        value={`${fmt(a.totalPaye)} FCFA`} extra={`Taux: ${a.tauxPaiement}% | ${a.nbFactures} fact.`} />
                    ))}
                  </div>,
                  <div key="f">
                    {abonnes?.flopImpaye?.map((a: any, i: number) => (
                      <TFRow key={i} rank={i} isTop={false} name={a.prenomnom || a.reference} sub={`${a.partenaire} - ${a.secteur || ''}`}
                        value={`${fmt(a.impaye)} FCFA`} extra={`${a.nbImpayes} impayes / ${a.nbFactures} fact.`} extraColor="#c62828" />
                    ))}
                  </div>
                ]}
              </TFSection>
              <div style={{ height: 20 }} />
              <TFSection topTitle="Top Consommation" flopTitle="Plus faible consommation" topIcon={'\u{1F4A7}'} flopIcon={'\u{1F4A4}'}>
                {[
                  <div key="t">
                    {abonnes?.topConso?.map((a: any, i: number) => (
                      <TFRow key={i} rank={i} isTop name={a.prenomnom || a.reference} sub={a.partenaire}
                        value={`${fmtFull(a.totalConso)} m3`} extra={`Moy: ${Number(a.consoMoyenne).toFixed(1)} m3/mois`} />
                    ))}
                  </div>,
                  <div key="f">
                    {abonnes?.flopConso?.map((a: any, i: number) => (
                      <TFRow key={i} rank={i} isTop={false} name={a.prenomnom || a.reference} sub={a.partenaire}
                        value={`${Number(a.consoMoyenne).toFixed(1)} m3/mois`} extra={`Total: ${fmtFull(a.totalConso)} m3 | ${a.nbFactures} fact.`} />
                    ))}
                  </div>
                ]}
              </TFSection>
            </>
          );

        case 'secteurs':
          return (
            <TFSection topTitle="Top Recouvrement" flopTitle="Flop Recouvrement" topIcon={'\u{1F31F}'} flopIcon={'\u{1F4C9}'}>
              {[
                <div key="t">
                  {secteurs?.topRecouvrement?.map((s: any, i: number) => (
                    <TFRow key={i} rank={i} isTop name={s.secteur} sub={`${s.partenaire} - ${s.nbAbonnes} abonnes`}
                      value={`${s.tauxRecouvrement}%`} extra={`${fmt(Number(s.totalPaye))} / ${fmt(Number(s.totalFacture))} FCFA`} />
                  ))}
                </div>,
                <div key="f">
                  {secteurs?.flopRecouvrement?.map((s: any, i: number) => (
                    <TFRow key={i} rank={i} isTop={false} name={s.secteur} sub={`${s.partenaire} - ${s.nbAbonnes} abonnes`}
                      value={`${s.tauxRecouvrement}%`} extra={`Impaye: ${fmt(Number(s.impaye))} FCFA`} extraColor="#c62828" />
                  ))}
                </div>
              ]}
            </TFSection>
          );

        case 'periodes':
          return (
            <TFSection topTitle="Mois les plus performants" flopTitle="Mois les moins performants" topIcon={'\u{1F4C8}'} flopIcon={'\u{1F4C9}'}>
              {[
                <div key="t">
                  {periodes?.topFacturation?.map((m: any, i: number) => (
                    <TFRow key={i} rank={i} isTop name={`${monthLabel(m.mois)} ${m.annee}`} sub={`Vol: ${fmt(Number(m.volume))} m3`}
                      value={`${fmt(Number(m.facturation))} FCFA`} extra={`Recouv: ${m.tauxRecouvrement}%`} />
                  ))}
                </div>,
                <div key="f">
                  {periodes?.flopRecouvrement?.map((m: any, i: number) => (
                    <TFRow key={i} rank={i} isTop={false} name={`${monthLabel(m.mois)} ${m.annee}`} sub={`Fact: ${fmt(Number(m.facturation))} FCFA`}
                      value={`${m.tauxRecouvrement}%`} extra={`Impaye: ${fmt(Number(m.impaye))} FCFA`} extraColor="#c62828" />
                  ))}
                </div>
              ]}
            </TFSection>
          );

        case 'localites':
          return (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 20 }}>
              <div style={{ ...S.card, borderTop: '4px solid #1a237e', marginBottom: 0 }}>
                <div style={{ ...S.cardTitle, color: '#1a237e' }}>{'\u{1F3C6}'} Top Localites par volume distribue</div>
                {localites?.topVolume?.map((l: any, i: number) => (
                  <TFRow key={i} rank={i} isTop name={l.localite} sub={`${l.partenaire} - ${l.nbAbonnes} abonnes`}
                    value={`${fmt(Number(l.totalVolume))} m3`} extra={`Recouv: ${l.tauxRecouvrement}% | Fact: ${fmt(Number(l.totalFacture))} FCFA`} />
                ))}
              </div>
            </div>
          );

        default: return null;
      }
    };

    return (
      <>
        {/* Sub-tabs navigation */}
        <div style={{ display: 'flex', gap: 0, background: '#fff', borderRadius: 10, padding: 3, marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,.06)', border: '1px solid #e0e0e0' }}>
          {TF_SUBTABS.map(st => (
            <button
              key={st.key}
              onClick={() => setTfSubTab(st.key)}
              style={{
                flex: 1, padding: '11px 8px', borderRadius: 8, border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 700, transition: '.2s',
                background: tfSubTab === st.key ? '#1a237e' : 'transparent',
                color: tfSubTab === st.key ? '#fff' : '#666',
                boxShadow: tfSubTab === st.key ? '0 2px 8px rgba(26,35,126,.3)' : 'none',
              }}
            >
              {st.icon} {st.label}
            </button>
          ))}
        </div>

        {renderSubContent()}
      </>
    );
  };

  const renderEfficiency = () => {
    if (!efficiency) return null;
    const { monthly, perForage } = efficiency;

    return (
      <>
        {monthly?.length > 0 && (
          <div style={S.card}>
            <div style={S.cardTitle}>Evolution du rendement et de la production</div>
            <MiniLineChart
              data={monthly.map((m: any) => ({
                label: `${monthLabel(m.mois)} ${m.annee}`,
                values: [
                  { value: m.production, color: '#1a237e', label: 'Production (m3)' },
                  { value: m.volumeDistribue, color: '#43a047', label: 'Volume distribue (m3)' },
                ]
              }))}
              height={240}
            />
          </div>
        )}

        <div style={S.grid2}>
          <div style={S.card}>
            <div style={S.cardTitle}>Rendement mensuel (%)</div>
            {monthly?.slice(-12).map((m: any, i: number) => (
              <HBar
                key={i}
                label={`${monthLabel(m.mois)} ${m.annee}`}
                value={m.rendement}
                max={100}
                color={m.rendement >= 75 ? '#43a047' : m.rendement >= 55 ? '#fb8c00' : '#e53935'}
                suffix="%"
              />
            ))}
          </div>
          <div style={S.card}>
            <div style={S.cardTitle}>Productivite (m3/h pompage)</div>
            {monthly?.slice(-12).filter((m: any) => m.productivite > 0).map((m: any, i: number) => (
              <HBar
                key={i}
                label={`${monthLabel(m.mois)} ${m.annee}`}
                value={m.productivite}
                max={Math.max(...monthly.map((x: any) => x.productivite))}
                color={CHART_COLORS[i % CHART_COLORS.length]}
              />
            ))}
          </div>
        </div>

        <div style={S.card}>
          <div style={S.cardTitle}>Performance par forage</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Forage</th>
                  <th style={S.th}>Partenaire</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Production totale</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Prod. moyenne/mois</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Cons. electrique</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Eff. energetique</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Nb mois</th>
                </tr>
              </thead>
              <tbody>
                {perForage?.slice(0, 20).map((f: any, i: number) => (
                  <tr key={i}>
                    <td style={S.td}>
                      <div style={{ fontWeight: 600 }}>{f.libelle || f.reference}</div>
                      <div style={{ fontSize: 11, color: '#999' }}>{f.reference}</div>
                    </td>
                    <td style={S.td}>{f.partenaire}</td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>{fmtFull(f.totalProduction)} m3</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{fmtFull(f.prodMoyenne)} m3</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{fmtFull(f.totalConsElec)} kWh</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{f.efficaciteEnergetique} m3/kWh</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{f.nbMois}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  const renderExpenses = () => {
    if (!expenses) return null;
    const { byType, chargesVsRevenue } = expenses;

    return (
      <>
        <div style={S.grid2}>
          <div style={S.card}>
            <div style={S.cardTitle}>Repartition des charges par type</div>
            <Donut
              segments={byType?.map((t: any, i: number) => ({
                label: t.type || 'Autre',
                value: t.total,
                color: CHART_COLORS[i % CHART_COLORS.length],
              })) || []}
            />
          </div>
          <div style={S.card}>
            <div style={S.cardTitle}>Top charges par montant</div>
            {byType?.map((t: any, i: number) => (
              <HBar
                key={i}
                label={t.type || 'Autre'}
                value={t.total}
                max={Math.max(...byType.map((x: any) => x.total))}
                color={CHART_COLORS[i % CHART_COLORS.length]}
              />
            ))}
          </div>
        </div>

        {chargesVsRevenue?.length > 0 && (
          <div style={S.card}>
            <div style={S.cardTitle}>Charges vs Recouvrement (evolution)</div>
            <MiniLineChart
              data={chargesVsRevenue.slice(-18).map((r: any) => ({
                label: `${monthLabel(r.mois)} ${r.annee}`,
                values: [
                  { value: r.recouvrement, color: '#43a047', label: 'Recouvrement' },
                  { value: r.charges, color: '#e53935', label: 'Charges' },
                  { value: Math.max(r.marge, 0), color: '#1a237e', label: 'Marge' },
                ]
              }))}
              height={260}
            />
          </div>
        )}
      </>
    );
  };

  const renderGeographic = () => {
    if (!geographic) return null;
    const { bySector, byLocality } = geographic;

    return (
      <>
        <div style={S.card}>
          <div style={S.cardTitle}>Performance par secteur</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Secteur</th>
                  <th style={S.th}>Partenaire</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Abonnes</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Facturation</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Recouvrement</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Impayes</th>
                  <th style={{ ...S.th, textAlign: 'center' }}>Taux</th>
                </tr>
              </thead>
              <tbody>
                {bySector?.map((s: any, i: number) => (
                  <tr key={i}>
                    <td style={{ ...S.td, fontWeight: 600 }}>{s.secteur || 'N/A'}</td>
                    <td style={S.td}>{s.partenaire}</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{s.nbAbonnes}</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{fmt(s.totalFacture)}</td>
                    <td style={{ ...S.td, textAlign: 'right', color: '#43a047' }}>{fmt(s.totalPaye)}</td>
                    <td style={{ ...S.td, textAlign: 'right', color: '#e53935' }}>{fmt(s.impayes)}</td>
                    <td style={{ ...S.td, textAlign: 'center' }}>
                      <span style={{ ...S.badge, background: s.tauxRecouvrement >= 80 ? '#e8f5e9' : s.tauxRecouvrement >= 60 ? '#fff8e1' : '#ffebee', color: s.tauxRecouvrement >= 80 ? '#2e7d32' : s.tauxRecouvrement >= 60 ? '#f57f17' : '#c62828' }}>
                        {s.tauxRecouvrement}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={S.card}>
          <div style={S.cardTitle}>Taux de recouvrement par localite</div>
          {byLocality?.slice(0, 15).map((l: any, i: number) => (
            <HBar
              key={i}
              label={l.localite || 'N/A'}
              value={l.tauxRecouvrement}
              max={100}
              color={l.tauxRecouvrement >= 80 ? '#43a047' : l.tauxRecouvrement >= 60 ? '#fb8c00' : '#e53935'}
              suffix="%"
            />
          ))}
        </div>
      </>
    );
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'overview': return renderOverview();
      case 'forecast': return renderForecast();
      case 'risk': return renderRisk();
      case 'consumption': return renderConsumption();
      case 'topflop': return renderTopFlop();
      case 'efficiency': return renderEfficiency();
      case 'expenses': return renderExpenses();
      case 'geographic': return renderGeographic();
      default: return null;
    }
  };

  return (
    <div style={S.page}>
      {/* Compact toolbar: tabs + filter on same line */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', background: '#fff', borderRadius: 10, padding: '4px 6px', marginBottom: 16, boxShadow: '0 1px 6px rgba(0,0,0,.07)', border: '1px solid #e8e8e8' }}>
        {TABS.map(t => (
          <button
            key={t.key}
            style={{
              padding: '7px 13px', borderRadius: 7, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 700, transition: '.2s', whiteSpace: 'nowrap',
              background: activeTab === t.key ? '#1a237e' : 'transparent',
              color: activeTab === t.key ? '#fff' : '#555',
              boxShadow: activeTab === t.key ? '0 2px 6px rgba(26,35,126,.25)' : 'none',
            }}
            onClick={() => setActiveTab(t.key)}
          >
            {t.icon} {t.label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <select
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12, background: '#fafafa', color: '#333', cursor: 'pointer' }}
            value={partenaireId}
            onChange={e => setPartenaireId(e.target.value)}
          >
            <option value="">Tous les partenaires</option>
            {partenaires.map((p: any) => (
              <option key={p.id} value={p.id}>{p.libelle}</option>
            ))}
          </select>
          <button
            onClick={() => loadTab(activeTab)}
            style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: '#1a237e', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}
          >
            {'\u{1F504}'}
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div style={S.loading}>
          <div style={S.spinner} />
          <div style={{ marginTop: 12 }}>Chargement des analyses...</div>
        </div>
      ) : (
        renderTab()
      )}

      {/* CSS for spinner */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default Analytics;
